import type { Schema } from 'prosemirror-model';
import type { Plugin } from 'prosemirror-state';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

import type { EditorJSONState } from '@core/prosemirror-types';

import type {
  IProsemirrorConfig,
  PluginsAPI,
  PluginsButtons,
  PluginWithPriority,
  RtPMPlugin,
} from './types';
import { EditorMode } from './types';
import { EMPTY_SELECTION } from '../helpers/utils';
import { createColumnsNodeView } from '../plugins/columns/nodes/columns.node';

type TypeOfArray<T> = T extends Array<infer U> ? U : T;
interface CreateEditorOptions {
  mode: EditorMode;
  initialState?: EditorJSONState | null;
}

const getNativeEditorPlugins = <T extends RtPMPlugin, S extends Schema>(
  plugins: T[],
  schema: S,
) => {
  const pluginsOrObjects = plugins.reduce(
    (acc, plugin) => [...acc, ...(plugin.getNativeEditorPlugins?.(schema) || [])],
    [],
  );
  const pluginObjects = pluginsOrObjects.map((pluginOrObject: PluginWithPriority) =>
    pluginOrObject.priority && pluginOrObject.plugin
      ? pluginOrObject
      : { priority: 50, plugin: <Plugin>(<unknown>pluginOrObject) },
  );
  const sortedObjects = [...pluginObjects].sort((a, b) =>
    a.priority < b.priority ? 1 : a.priority > b.priority ? -1 : 0,
  );
  return sortedObjects.map(({ plugin }) => plugin);
};

const getNativeViewPlugins = <T extends RtPMPlugin>(plugins: T[], mode: EditorMode) =>
  plugins.reduce((acc, plugin) => [...acc, ...(plugin.getNativeViewPlugins?.(mode) || [])], []);

const setContent = (editorView: EditorView, content: EditorJSONState) => {
  editorView.setProps({
    state: EditorState.fromJSON(editorView.state, {
      ...content,
      selection: content.selection || EMPTY_SELECTION,
    }),
  });
};

const createReadOnlyEditor = (
  editorElement: HTMLElement,
  schema: Schema,
  plugins: Plugin[],
  options: CreateEditorOptions,
) => {
  const editorView = new EditorView(editorElement, {
    state: EditorState.create({
      schema,
      plugins,
    }),
  });

  if (options.initialState) setContent(editorView, options.initialState);

  const {
    input: { eventHandlers },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    domObserver,
  } = <any>editorView;
  Object.entries(eventHandlers).forEach(([name, handler]: [string, () => void]) =>
    editorView.dom.removeEventListener(name, handler),
  );
  editorView.setProps({ editable: () => false });
  // domObserver.disconnectSelection();

  return editorView;
};

const getApiFromPlugins = <T extends RtPMPlugin>(plugins: T[]) =>
  plugins.reduce(
    (acc, plugin) => {
      Object.keys(plugin).forEach(key => {
        if (key in <object>acc) {
          throw new Error(`Key conflict`);
        }
      });

      Object.assign(acc, plugin.api || {});
      return acc;
    },
    <Extract<PluginsAPI<TypeOfArray<T>>, object>>{},
  );

export const createEditor = <T extends RtPMPlugin, Config extends IProsemirrorConfig>(
  editorElement: HTMLElement,
  plugins: T[],
  config: Config,
  options: CreateEditorOptions,
) => {
  const schema = config.getSchema();
  const destroyPlugins = () => plugins.forEach(plugin => plugin.destroy?.());

  plugins.forEach(plugin => plugin.beforeInit?.(options.mode, schema));
  const viewPlugins = getNativeViewPlugins(plugins, options.mode);

  if (EditorMode.View === options.mode) {
    const editorView = createReadOnlyEditor(editorElement, schema, viewPlugins, options);
    const publicApi: PluginsAPI<TypeOfArray<T>> = getApiFromPlugins(plugins);
    plugins.forEach(plugin => plugin.afterInit?.(options.mode, editorView));
    return {
      destroyPlugins,
      editorView,
      publicApi,
    };
  } else {
    const editorPlugins = getNativeEditorPlugins(plugins, schema);
    const editorView = new EditorView(editorElement, {
      state: EditorState.create({
        schema,
        plugins: [...viewPlugins, ...editorPlugins],
      }),
      attributes: {
        class: 'editor',
      },
      nodeViews: {
        columns: createColumnsNodeView(),
      },
    });
    plugins.forEach(plugin => plugin.afterInit?.(options.mode, editorView));

    if (options.initialState) {
      setContent(editorView, options.initialState);
    }

    const publicApi: PluginsAPI<TypeOfArray<T>> = getApiFromPlugins(plugins);
    const buttons: PluginsButtons<TypeOfArray<T>> = plugins.reduce(
      (acc, plugin) => {
        Object.keys(plugin).forEach(key => {
          if (key in <object>acc) {
            throw new Error(`Key conflict`);
          }
        });
        Object.assign(acc, plugin.buttons || {});
        return acc;
      },
      <Extract<PluginsButtons<TypeOfArray<T>>, object>>{},
    );

    if (buttons && config.registerToolbarButtons) {
      config.registerToolbarButtons(<any>buttons);
    }

    return {
      editorView,
      schema,
      destroyPlugins,
      publicApi,
      buttons,
    };
  }
};
